import type { Action } from 'redux';
import { createAction } from '@reduxjs/toolkit';
import type { AvailableTagsReturnType } from '~/api/asset-tag-categories-api';
import type { Tag, TagCategory } from '~/ducks/asset-tags/data/Tag';
import type { ActionWithPayload, RequestSagaAction } from '~/types/actions.types';
import AssetTagsConstants from './constants';

type ClearTagActionErrorModalType = Action<AssetTagsConstants.CLEAR_TAG_ACTION_ERROR>;
export const clearTagActionErrorModalType = () => ({
  type: AssetTagsConstants.CLEAR_TAG_ACTION_ERROR,
});

export const getAvailableTags = () => ({
  type: AssetTagsConstants.GET_AVAILABLE_TAGS,
});

export type GetAvailableTagsType = RequestSagaAction<
  AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST,
  AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST_SUCCESS,
  AssetTagsConstants.GET_AVAILABLE_TAGS_REQUEST_ERROR,
  {},
  AvailableTagsReturnType
>;

export const createTagCategoryAction = (name: string) => ({
  type: AssetTagsConstants.CREATE_TAG_CATEGORY,
  payload: { name },
});

export const SET_IS_EDITING = '@AssetTags/SET_IS_EDITING';

export const setIsEditing = createAction<boolean>(SET_IS_EDITING);

// RTK PayloadAction widens `type` to string, breaking union discrimination.
export type SetIsEditingType = ActionWithPayload<typeof SET_IS_EDITING, boolean>;

export type CreateTagCategoryType = RequestSagaAction<
  AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST,
  AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST_SUCCESS,
  AssetTagsConstants.CREATE_TAG_CATEGORY_REQUEST_ERROR,
  { tmpTagCategory: TagCategory },
  TagCategory
>;

export const updateTagCategoryAction = (tagCategory: TagCategory, name: string) => ({
  type: AssetTagsConstants.UPDATE_TAG_CATEGORY,
  payload: { tagCategory, name },
});

export type UpdateTagCategoryType = RequestSagaAction<
  AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST,
  AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST_SUCCESS,
  AssetTagsConstants.UPDATE_TAG_CATEGORY_REQUEST_ERROR,
  { tagCategory: TagCategory; name: string },
  TagCategory
>;

export const deleteTagCategoryAction = (tagCategory: TagCategory) => ({
  type: AssetTagsConstants.DELETE_TAG_CATEGORY,
  payload: { tagCategory },
});

export type DeleteTagCategoryType = RequestSagaAction<
  AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST,
  AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST_SUCCESS,
  AssetTagsConstants.DELETE_TAG_CATEGORY_REQUEST_ERROR,
  { tagCategory: TagCategory; prevIndex: number },
  {}
>;

export const createTagAction = (tagCategory: TagCategory, name: string) => ({
  type: AssetTagsConstants.CREATE_TAG,
  payload: { tagCategory, name },
});

export type CreateTagTypePayload = { tagCategory: TagCategory; tmpTag: Tag };
export type CreateTagType = RequestSagaAction<
  AssetTagsConstants.CREATE_TAG_REQUEST,
  AssetTagsConstants.CREATE_TAG_REQUEST_SUCCESS,
  AssetTagsConstants.CREATE_TAG_REQUEST_ERROR,
  CreateTagTypePayload,
  Tag
>;

export const updateTagAction = (tag: Tag, name: string) => ({
  type: AssetTagsConstants.UPDATE_TAG,
  payload: { tag, name },
});

export type UpdateTagPayload = { tag: Tag; name: string };
export type UpdateTagType = RequestSagaAction<
  AssetTagsConstants.UPDATE_TAG_REQUEST,
  AssetTagsConstants.UPDATE_TAG_REQUEST_SUCCESS,
  AssetTagsConstants.UPDATE_TAG_REQUEST_ERROR,
  UpdateTagPayload,
  Tag
>;

export const deleteTagAction = (tag: Tag) => ({
  type: AssetTagsConstants.DELETE_TAG,
  payload: { tag },
});

export type DeleteTagPayload = { tag: Tag; prevIndex: number };
export type DeleteTagType = RequestSagaAction<
  AssetTagsConstants.DELETE_TAG_REQUEST,
  AssetTagsConstants.DELETE_TAG_REQUEST_SUCCESS,
  AssetTagsConstants.DELETE_TAG_REQUEST_ERROR,
  DeleteTagPayload,
  {}
>;

export type AssetTagsActions =
  | ClearTagActionErrorModalType
  | GetAvailableTagsType
  | CreateTagCategoryType
  | UpdateTagCategoryType
  | DeleteTagCategoryType
  | CreateTagType
  | UpdateTagType
  | DeleteTagType
  | SetIsEditingType;
